import React from 'react';
import { notification } from 'antd';
import lib from '../../assets/utils/lib';
import { hasPopulatedPermissionMap } from '../../assets/utils/permissions';
import { generateAndDownloadFacilityExport } from './generateFacilityExport';

/** Permissions that may queue facility listing exports (API enforces as well). */
export const FACILITY_EXPORT_PERMISSIONS = ['facilities.export', 'admin.*'];

/** Staff role ids that could export before RBAC; used only while the map is empty. */
export const LEGACY_FACILITY_EXPORT_ROLES = [1, 2, 3, 4, 5];

export const FORMAT_LABELS = {
  csv: 'CSV',
  xlsx: 'Excel',
  pdf: 'PDF',
};

/**
 * Permission-first; the legacy staff role ids apply only while the session has no
 * RBAC permission map. Pass canStayOnModule (from usePermissions) as the third
 * argument where available; the 2-arg form is kept for callers not yet migrated.
 *
 * @param {object} user
 * @param {(perms: string[]) => boolean} [hasAnyPermission]
 * @param {(moduleKey: string, legacyRoleIds?: number[]) => boolean} [canStayOnModule]
 */
export function canExportFacilities(user, hasAnyPermission, canStayOnModule) {
  if (typeof canStayOnModule === 'function') {
    return canStayOnModule('facilitiesExport', LEGACY_FACILITY_EXPORT_ROLES);
  }
  if (typeof hasAnyPermission === 'function' && hasAnyPermission(FACILITY_EXPORT_PERMISSIONS)) {
    return true;
  }
  if (hasPopulatedPermissionMap(null, user)) return false;
  return LEGACY_FACILITY_EXPORT_ROLES.includes(Number(user?.role?.staffRoleId));
}

export function notifyFacilityExportQueued(format, onViewExports) {
  const label = FORMAT_LABELS[format] || 'Export';
  notification.success({
    message: 'Export queued',
    description: `Your ${label} export is processing. Open Exports to download when ready.`,
    btn: onViewExports ? (
      <button type="button" className="ant-btn ant-btn-sm ant-btn-primary" onClick={onViewExports}>
        View exports
      </button>
    ) : undefined,
  });
}

export function notifyFacilityExportFailed(error) {
  notification.error({
    message: 'Export failed',
    description:
      error?.response?.data?.message ||
      error?.message ||
      'Unable to export facilities',
  });
}

export function notifyFacilityDownloadComplete(format, filename) {
  const label = FORMAT_LABELS[format] || 'File';
  notification.success({
    message: 'Download started',
    description: filename ? `${label} saved as ${filename}` : `Your ${label} download has started.`,
  });
}

/** Misleading column — value is the onboarded-by id, not a facility code. */
export const EXCLUDED_FACILITY_EXPORT_FIELD_KEYS = ['facility_code'];

export function sanitizeFacilityExportFieldKeys(keys) {
  if (!Array.isArray(keys)) return [];
  const excluded = new Set(EXCLUDED_FACILITY_EXPORT_FIELD_KEYS);
  return keys.filter((k) => k && !excluded.has(k));
}

function sanitizeFacilityExportCatalog({ fields, presets, limits, supportedFormats }) {
  const excluded = new Set(EXCLUDED_FACILITY_EXPORT_FIELD_KEYS);
  const filteredFields = fields.filter((f) => f?.key && !excluded.has(f.key));
  const filteredPresets = {};
  Object.entries(presets).forEach(([key, vals]) => {
    filteredPresets[key] = Array.isArray(vals)
      ? vals.filter((k) => k && !excluded.has(k))
      : vals;
  });
  return { fields: filteredFields, presets: filteredPresets, limits, supportedFormats };
}

/**
 * Parse field catalog from GET /v2/admin/facilities/export/fields
 * @param {object} resp - axios response
 */
export function parseFacilityExportFieldsResponse(resp) {
  const body = resp?.data;
  if (body && Number(body.error) !== 0 && body.error !== false) {
    throw new Error(body.message || 'Failed to load export fields');
  }
  const data = body?.data ?? body;
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const presets = data?.presets && typeof data.presets === 'object' ? data.presets : {};
  const limits = data?.limits && typeof data.limits === 'object' ? data.limits : {};
  const supportedFormats = Array.isArray(data?.formats)
    ? data.formats
    : ['csv', 'xlsx', 'pdf'];
  return sanitizeFacilityExportCatalog({ fields, presets, limits, supportedFormats });
}

/**
 * Default selected field keys from catalog (fields with default: true).
 * @param {Array<{ key: string, default?: boolean }>} fields
 */
export function defaultFacilityExportFieldKeys(fields) {
  const defaults = fields.filter((f) => f.default).map((f) => f.key);
  const keys = defaults.length ? defaults : fields.slice(0, 6).map((f) => f.key);
  return sanitizeFacilityExportFieldKeys(keys);
}

/**
 * @param {object} options
 * @param {'filtered'|'all'} options.scope
 * @param {'csv'|'xlsx'|'pdf'} [options.format]
 * @param {string} [options.search]
 * @param {string} [options.state]
 * @param {string|number} [options.status]
 * @param {string[]} options.fields
 */
export async function queueFacilityExportJob({
  scope,
  search,
  state,
  status,
  fields,
  format = 'csv',
}) {
  const params = {
    scope,
    fields: sanitizeFacilityExportFieldKeys(fields),
    format,
  };
  const q = (search ?? '').trim();
  if (scope === 'filtered' && q) params.search = q;
  if (state) params.state = state;
  if (status != null && status !== '') params.status = String(status);

  const response = await lib.createFacilityExportJob(params);
  if (response?.data?.error) {
    throw new Error(response.data.message || 'Unable to queue export job');
  }
  return response;
}

/**
 * Server sync CSV (legacy path).
 * @returns {{ success: true, filename: string, useAsync?: boolean }}
 */
export async function trySyncFacilityExport({ scope, search, state, status, fields, format = 'csv' }) {
  const params = {
    scope,
    search,
    state,
    status,
    fields: sanitizeFacilityExportFieldKeys(fields),
    format,
  };
  return lib.exportFacilitiesSyncV2(params);
}

/**
 * Client-side export (PDF letterhead, Excel workbook, or CSV).
 */
export async function downloadFacilityExportClient({
  format,
  scope,
  search,
  state,
  status,
  fields,
  catalogFields,
  meta,
  maxRows,
}) {
  const result = await generateAndDownloadFacilityExport({
    format,
    scope,
    search,
    state,
    status,
    fields: sanitizeFacilityExportFieldKeys(fields),
    catalogFields: (catalogFields || []).filter(
      (f) => !EXCLUDED_FACILITY_EXPORT_FIELD_KEYS.includes(f.key),
    ),
    meta,
    maxRows,
  });
  if (result?.useAsync) {
    return { useAsync: true };
  }
  return result;
}

/**
 * Backend async jobs currently produce CSV. PDF/Excel use client generation.
 */
export function isServerQueuedExportFormat(format) {
  return String(format || 'csv').toLowerCase() === 'csv';
}

/**
 * PDF and Excel are generated client-side (letterhead / cover sheet).
 */
export function shouldUseClientInstantExport(format) {
  return format === 'pdf' || format === 'xlsx';
}
